import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import NurseDoctorOutputComponent from '../output/NurseDoctorOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const NurseVerificationInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ idNumber: '', pan: '', mobile: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleField = (field, transform = (v) => v) => (e) => {
        const value = transform(e.target.value || '');
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.idNumber.trim()) next.idNumber = 'Nurse ID number is required';
        if (formData.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan.toUpperCase())) next.pan = 'Enter a valid 10-character PAN';
        if (formData.mobile && !/^[6-9]\d{9}$/.test(formData.mobile)) next.mobile = 'Enter a valid 10-digit Indian mobile number';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/tartan/nurse-verification/verify', {
                id_number: formData.idNumber.trim(),
                pan: formData.pan.trim().toUpperCase() || undefined,
                mobile: formData.mobile.trim() || undefined,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Verification failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'Nurse verified', detail: 'Report ready below', life: 2000 });
            // Credits just changed; drop the cached balance so every consumer updates.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Nurse verification failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const responseData = error.response?.data;
            let detail;
            if (status === 402) {
                const c = responseData || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            } else if (status === 429) {
                detail = 'Too many requests. Please wait a minute before trying again.';
            } else {
                detail = responseData?.message || error.message || 'Verification failed';
            }
            toast.current?.show({ severity: 'error', summary: 'Verification failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Nurse ID:</span> <span className="text-900 font-medium">{formData.idNumber}</span></div>
                    {formData.pan && <div className="mb-2"><span className="text-600">PAN:</span> <span className="text-900 font-medium">{formData.pan.toUpperCase()}</span></div>}
                    {formData.mobile && <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{formData.mobile}</span></div>}
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm nurse verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, verify',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ idNumber: '', pan: '', mobile: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-heart"
                        title="Nurse Registration Verification"
                        subtitle="Verify nurse registration details via State Nursing Council records"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="nurseId" className="block font-medium text-sm mb-2">
                                            Nurse ID Number <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-id-card" />
                                            <InputText
                                                id="nurseId"
                                                value={formData.idNumber}
                                                onChange={handleField('idNumber')}
                                                placeholder="e.g. INC-12345"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.idNumber })}
                                                aria-describedby="nurseId-help"
                                                aria-invalid={!!errors.idNumber}
                                            />
                                        </IconField>
                                        {errors.idNumber && (
                                            <small id="nurseId-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.idNumber}
                                            </small>
                                        )}
                                    </div>
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="pan" className="block font-medium text-sm mb-2">PAN <span className="text-500">(optional)</span></label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-credit-card" />
                                            <InputText
                                                id="pan"
                                                value={formData.pan}
                                                onChange={handleField('pan', (v) => v.toUpperCase().slice(0, 10))}
                                                placeholder="ABCDE1234F"
                                                maxLength={10}
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.pan })}
                                            />
                                        </IconField>
                                        {errors.pan && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.pan}</small>}
                                    </div>
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="mobile" className="block font-medium text-sm mb-2">Mobile <span className="text-500">(optional)</span></label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-phone" />
                                            <InputText
                                                id="mobile"
                                                value={formData.mobile}
                                                onChange={handleField('mobile', (v) => v.replace(/\D/g, '').slice(0, 10))}
                                                placeholder="9876543210"
                                                inputMode="tel"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.mobile })}
                                            />
                                        </IconField>
                                        {errors.mobile && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.mobile}</small>}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType="nurse-verification"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Verify Nurse"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Verify another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <NurseDoctorOutputComponent reportData={reportData} title="Nurse Verification Report" icon="pi-heart" />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default NurseVerificationInputComponent;
